import _ from 'lodash'
import Backbone from 'backbone'
import appDisplay from 'scoreboard/components/app-display'
import dialogStack from 'scoreboard/components/dialog-stack'
import globals from 'scoreboard/components/globals'
import layoutManager from 'scoreboard/components/layout-manager'
import unwrapModel from 'scoreboard/components/unwrap-model'
import user from 'scoreboard/components/user'
import * as websocket from 'scoreboard/components/websocket'
import Alert from 'scoreboard/alerts/alert'
import BroadcastAlertPopup from 'scoreboard/admin/alerts/broadcast-alert-popup'
import ExportStatusDialog from 'scoreboard/components/export/export-status-dialog'

/**
 * Sets up push notifications that are global in scope and not directly
 * owned by a single view.  For other subscriptions, search for `stompClient.subscribe`, or see:
 * - org-updates.js
 * - dataset-status.js
 * - import-status.js
 */
function setupPushNotications() {

	// #20518 can't do this until we're logged into a database
	Backbone.once('load:database', (databaseId) => {
		websocket.latest().then(function (stompClient) {
			subscribeNotifications(databaseId, stompClient)
		})
		Backbone.on('websocket:reconnect', stompClient => {
			subscribeNotifications(databaseId, stompClient)
		})
	})
}

function baseUserTopic(databaseId = globals.databaseId) {
	return '/topic/database/' + databaseId + '/user/' + user.model.id
}

/**
 * @private
 */
function subscribeNotifications(databaseId, stompClient) {

	// No user topics for the Anonymous user (#25912)
	if (user.isAnonymous()) {
		return
	}

	const impersonator = user.impersonator
	if (impersonator) {
		// Allow the impersonator to receive any broadcast alert that was sent
		// to them while they're impersonating someone else (e.g. export ready)
		stompClient.subscribe('/topic/database/' + databaseId + '/user/' + impersonator.id + '/alerts/broadcast',
			message => onBroadcastAlert(message))
	}

	stompClient.subscribe(baseUserTopic(databaseId) + '/alerts/broadcast',
		message => onBroadcastAlert(message))

	stompClient.subscribe('/topic/database/' + databaseId + '/user/' + user.model.id + '/alerts',
		message => {
			var model = JSON.parse(message.body)
			Backbone.trigger('change:unread',
				model,
				{push: true})
		})

	stompClient.subscribe(`/topic/database/${databaseId}/user/${user.model.id}/pendingmetricvalueapprovals`,
		message => {
			const model = JSON.parse(message.body)
			Backbone.trigger('change:numPending', model, {push: true})
		})
}

/**
 * Encapsulates details of how to listen for websocket messages.
 *
 * @param {{topic, onReceive}[]} Topics to listen to, each object must have 2 properties
 * {String} topic to be interpolated into: /topic/database/id/user/id/{topic}
 * {Function} onReceive will be called with the body of the websocket message
 */
function registerPushNotificationListenersForUser(topics) {
	Backbone.once('load:database', (databaseId) => {
		websocket.latest().then(stompClient => {
			subscribeTopics(databaseId, stompClient, topics)
		})

		Backbone.on('websocket:reconnect', stompClient => {
			subscribeTopics(databaseId, stompClient, topics)
		})
	})
}

/**
 * @private
 */
function subscribeTopics(databaseId, stompClient, topics) {
	// No user topics for the Anonymous user (#25912)
	if (user.isAnonymous()) {
		return
	}
	for (let {topic, onReceive} of topics) {
		const destination = `/topic/database/${databaseId}/user/${user.model.id}${topic}`
		stompClient.subscribe(destination, message => {
			onReceive(JSON.parse(message.body))
		})
	}
}

function onBroadcastAlert(message) {
	if (layoutManager.export) {
		// #49635 if a broadcast alert shows up during an export, we don't want
		// that alert to show in the export; just ignore it
		return
	}

	const body = JSON.parse(message.body)
	const {stackTrace} = body
	const model = new Alert(body)
	const {exportId} = _.get(unwrapModel(model), 'properties', {})
	if (model.isExportReady()) {
		onExportReady(exportId, model)
	} else if (model.isExportFailed()) {
		onExportFailed(exportId, model, stackTrace)
	} else {
		appDisplay.dialog(new BroadcastAlertPopup({model}))
	}
}

function onExportReady(exportId, model) {
	showExportStatusDialog(exportId, model,
			statusDialog => statusDialog.fileReady(model))
}

function onExportFailed(exportId, model, stackTrace) {
	showExportStatusDialog(exportId, model,
		statusDialog => statusDialog.exportFailed(model, stackTrace))
}

function showExportStatusDialog(exportId, model, readyFn) {
	const {view: statusDialog} = dialogStack.findDialogWithView(view => {
		return view instanceof ExportStatusDialog && view.exportId == exportId
	})

	if (statusDialog) {
		readyFn(statusDialog)
	} else {
		appDisplay.dialog(new ExportStatusDialog({ model, alertPopup: true }))
	}
}

/**
 * The server may send push notifications to the client, this setup is responsible
 * subscribing to events which must display global messages to the user.
 */
export default setupPushNotications
export { registerPushNotificationListenersForUser }
export { baseUserTopic }
setupPushNotications.__filename__ = 'setup-push-notifications.js'
